import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { Magazine } from 'app/models/magazine';
import { MagazineService } from 'app/services/magazine/magazine.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-modifier-magazine',
  templateUrl: './modifier-magazine.component.html',
  styleUrls: ['./modifier-magazine.component.scss']
})
export class ModifierMagazineComponent implements OnInit, OnDestroy {
  magazineForm!: FormGroup;
  
  magazine: Magazine | null = null;
  magazineId: string | null = null;
  originalMagazineData: any = null;
  
  imagePreview: string | null = null;
  imageFile: File | null = null;
  uploadedImageUrl: string | null = null;
  
  pdfFile: File | null = null;
  pdfFileName: string | null = null;
  isUploadingPdf: boolean = false;
  uploadedPdfUrl: string | null = null;
  
  pdfInputMode: 'upload' | 'url' = 'upload';
  
  isUploading: boolean = false;
  isSubmitting: boolean = false;
  hasUnsavedChanges: boolean = false;
  isLoading: boolean = true;

  private destroy$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private magazineService: MagazineService,
    private awsService: AwscontrolService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadMagazineData();
    
    this.magazineForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.originalMagazineData) {
          const currentData = this.magazineForm.getRawValue();
          this.hasUnsavedChanges = JSON.stringify(currentData) !== JSON.stringify(this.originalMagazineData) || 
                                  !!this.imageFile || 
                                  !!this.pdfFile || 
                                  this.imagePreview !== this.magazine?.imageUrl ||
                                  this.pdfInputMode === 'url';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.magazineForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      nbr_telechargement: [0, [Validators.required, Validators.min(0), Validators.max(1000000)]],
      downloadCount: [0, [Validators.required, Validators.min(0), Validators.max(1000000)]],
      pdfManualUrl: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/)]]
    });
  }

  get pdfManualUrlControl(): AbstractControl | null {
    return this.magazineForm?.get('pdfManualUrl');
  }

  private loadMagazineData(): void {
    this.magazineId = this.activatedRoute.snapshot.paramMap.get('id');
    
    if (!this.magazineId) {
      this.showError('ID manquant', 'Aucun ID de magazine spécifié');
      this.router.navigate(['/listeMagazine']);
      return;
    }

    this.isLoading = true;
    
    this.magazineService.getMagazine(this.magazineId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: Magazine) => {
          this.magazine = data;
          this.imagePreview = data.imageUrl ?? null;
          
          const downloadLink = data.download_link ?? '';
          if (downloadLink) {
            this.pdfFileName = this.extractFileNameFromUrl(downloadLink);
          }
          
          this.magazineForm.patchValue({
            name: data.name || '',
            nbr_telechargement: data.nbr_telechargement ?? 0,
            downloadCount: (data as any).downloadCount ?? 0,
            pdfManualUrl: downloadLink
          }, { emitEvent: false });
          
          this.originalMagazineData = JSON.parse(JSON.stringify(this.magazineForm.getRawValue()));
          this.hasUnsavedChanges = false;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading magazine:', error);
          this.showError('Erreur', 'Impossible de charger les données du magazine');
          this.router.navigate(['/listeMagazine']);
        }
      });
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.magazineForm?.get(controlName);
    return !!(control?.invalid && control?.touched);
  }

  getControlErrors(controlName: string): any {
    const control = this.magazineForm?.get(controlName);
    return control?.errors || {};
  }

  switchPdfMode(mode: 'upload' | 'url'): void {
    if (this.pdfInputMode === mode) return;
    
    this.pdfInputMode = mode;
    this.hasUnsavedChanges = true;

    if (mode === 'url') {
      this.pdfFile = null;
      this.pdfFileName = null;
      this.uploadedPdfUrl = null;
    } else {
      this.pdfManualUrlControl?.setValue('');
      this.pdfManualUrlControl?.markAsPristine();
      if (this.magazine?.download_link && !this.pdfFileName) {
        this.pdfFileName = this.extractFileNameFromUrl(this.magazine.download_link);
      }
    }
  }

  selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        this.showError('Type de fichier non supporté', 'Veuillez sélectionner une image (JPEG, PNG, GIF, WebP)');
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.showError('Fichier trop volumineux', 'La taille maximale est de 5MB');
        return;
      }

      this.imageFile = file;
      this.previewImage(file);
      this.hasUnsavedChanges = true;
    }
  }

  private previewImage(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.imagePreview = e.target?.result as string;
    };
    reader.onerror = () => {
      this.showError('Erreur de lecture', 'Impossible de lire le fichier image');
      this.imagePreview = null;
    };
    reader.readAsDataURL(file);
  }

  async uploadImageToAws(): Promise<string | null> {
    if (!this.imageFile) return this.magazine?.imageUrl ?? null;

    this.isUploading = true;
    
    try {
      Swal.fire({
        title: 'Téléchargement en cours...',
        html: 'Upload de l\'image vers le serveur',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading()
      });

      const response = await this.awsService.uploadFile(this.imageFile, 'magazines').toPromise();
      
      Swal.close();
      
      if (response?.success && response.urls && response.urls.length > 0) {
        this.uploadedImageUrl = response.urls[0];
        this.isUploading = false;
        
        Swal.fire({
          icon: 'success',
          title: 'Image téléchargée',
          text: 'L\'image a été uploadée avec succès',
          timer: 1500,
          showConfirmButton: false
        });
        
        return response.urls[0];
      } else {
        throw new Error(response?.error || 'Upload failed');
      }
      
    } catch (error: any) {
      this.isUploading = false;
      Swal.close();
      console.error('AWS Upload Error:', error);
      
      let errorMessage = 'Échec du téléchargement de l\'image';
      if (error.message?.includes('timeout')) {
        errorMessage = 'Délai d\'attente dépassé. Veuillez réessayer.';
      } else if (error.status === 0) {
        errorMessage = 'Impossible de se connecter au serveur. Vérifiez votre connexion.';
      } else if (error.status === 413) {
        errorMessage = 'Fichier trop volumineux pour le serveur';
      }
      
      this.showError('Erreur de téléchargement', errorMessage);
      return null;
    }
  }

  removeImage(): void {
    Swal.fire({
      title: 'Supprimer l\'image ?',
      text: 'Cette action ne peut pas être annulée',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (this.magazine?.imageUrl) {
          try {
            await this.awsService.deleteFile(this.magazine.imageUrl).toPromise();
          } catch (error) {
            console.warn('Could not delete image from AWS:', error);
          }
        }
        
        this.imagePreview = null;
        this.imageFile = null;
        this.uploadedImageUrl = null;
        this.hasUnsavedChanges = true;
        
        Swal.fire({
          icon: 'success',
          title: 'Image supprimée',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  }

  selectPdfFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        this.showError('Type de fichier non supporté', 'Veuillez sélectionner un fichier PDF');
        return;
      }

      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        this.showError('Fichier trop volumineux', 'La taille maximale est de 50MB');
        return;
      }

      this.pdfFile = file;
      this.pdfFileName = file.name;
      this.hasUnsavedChanges = true;
    }
  }

  async uploadPdfToAws(): Promise<string | null> {
    if (!this.pdfFile) return null;

    this.isUploadingPdf = true;
    
    try {
      Swal.fire({
        title: 'Téléchargement PDF en cours...',
        html: 'Upload du fichier vers le serveur',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading()
      });

      const response = await this.awsService.uploadPdf([this.pdfFile]).toPromise();
      
      Swal.close();
      
      if (response?.success && response.urls && response.urls.length > 0) {
        this.uploadedPdfUrl = response.urls[0];
        this.isUploadingPdf = false;
        
        Swal.fire({
          icon: 'success',
          title: 'PDF téléchargé',
          text: 'Le fichier PDF a été uploadé avec succès',
          timer: 1500,
          showConfirmButton: false
        });
        
        return response.urls[0];
      } else {
        const errorMsg = response?.errors?.join(', ') || 'Upload failed';
        throw new Error(errorMsg);
      }
      
    } catch (error: any) {
      this.isUploadingPdf = false;
      Swal.close();
      console.error('AWS PDF Upload Error:', error);
      
      let errorMessage = 'Échec du téléchargement du PDF';
      if (error.message?.includes('timeout')) {
        errorMessage = 'Délai d\'attente dépassé. Veuillez réessayer.';
      } else if (error.status === 0) {
        errorMessage = 'Impossible de se connecter au serveur. Vérifiez votre connexion.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.showError('Erreur de téléchargement', errorMessage);
      return null;
    }
  }

  removePdf(): void {
    Swal.fire({
      title: 'Supprimer le PDF ?',
      text: 'Cette action ne peut pas être annulée. Les liens de téléchargement seront vidés.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (this.magazine?.download_link && this.magazine.download_link.includes('/magazinepdf/')) {
          try {
            await this.awsService.deletePdf(this.magazine.download_link).toPromise();
          } catch (error) {
            console.warn('Could not delete PDF from AWS:', error);
          }
        }
        
        this.pdfFile = null;
        this.pdfFileName = null;
        this.uploadedPdfUrl = null;
        this.pdfManualUrlControl?.setValue('');
        this.hasUnsavedChanges = true;
        
        Swal.fire({
          icon: 'success',
          title: 'PDF supprimé',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  }

  private extractFileNameFromUrl(url: string): string {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || '';
  }

  getPdfDisplayName(): string {
    if (this.pdfFileName) return this.pdfFileName;
    if (this.magazine?.download_link) {
      return this.extractFileNameFromUrl(this.magazine.download_link);
    }
    return '';
  }

  private validateForm(): boolean {
    if (!this.magazineForm || this.magazineForm.invalid) {
      this.markFormGroupTouched(this.magazineForm);
      this.showError('Formulaire incomplet', 'Veuillez corriger les erreurs dans le formulaire');
      return false;
    }

    if (this.pdfInputMode === 'upload') {
      const hasExisting = !!this.magazine?.download_link;
      const hasNewFile = !!this.pdfFile;
      const hasUploadedSession = !!this.uploadedPdfUrl;
      
      if (!hasExisting && !hasNewFile && !hasUploadedSession) {
        this.showError('PDF manquant', 'Veuillez uploader un fichier PDF');
        return false;
      }
    } else {
      const url = this.pdfManualUrlControl?.value;
      if (!url || !url.trim()) {
        this.showError('URL manquante', 'Veuillez entrer une URL valide pour le PDF');
        return false;
      }
      if (this.pdfManualUrlControl?.invalid) {
        this.showError('URL invalide', 'Veuillez entrer une URL HTTP/HTTPS valide');
        return false;
      }
    }

    return true;
  }

  async modifierMagazine(): Promise<void> {
    if (!this.validateForm()) return;

    if (this.isUploading || this.isUploadingPdf) {
      this.showWarning('Veuillez patienter', 'Un téléchargement est en cours');
      return;
    }

    this.isSubmitting = true;
    
    try {
      let imageUrl: string | null = this.magazine?.imageUrl ?? null;
      if (this.imageFile) {
        const uploadedImg = await this.uploadImageToAws();
        if (!uploadedImg) {
          this.isSubmitting = false;
          return;
        }
        imageUrl = uploadedImg;
      } else if (this.imagePreview === null) {
        imageUrl = null;
      }

      let pdfUrl: string | null = null;

      if (this.pdfInputMode === 'upload') {
        if (this.pdfFile) {
          const uploadedPdf = await this.uploadPdfToAws();
          if (!uploadedPdf) {
            this.isSubmitting = false;
            return;
          }
          pdfUrl = uploadedPdf;
        } else if (this.uploadedPdfUrl) {
          pdfUrl = this.uploadedPdfUrl;
        } else {
          pdfUrl = this.magazine?.download_link ?? null;
        }
      } else {
        pdfUrl = this.pdfManualUrlControl?.value;
      }

      const magazineData = {
        name: this.magazineForm.get('name')?.value || '',
        download_link: pdfUrl || '',
        flip_book_link: pdfUrl || '',
        nbr_telechargement: this.magazineForm.get('nbr_telechargement')?.value ?? 0,
        downloadCount: this.magazineForm.get('downloadCount')?.value ?? 0,
        imageUrl: imageUrl
      };

      await this.updateMagazine(magazineData);
      
    } catch (error) {
      console.error('Update error:', error);
      this.showError('Erreur', 'Une erreur est survenue lors de la modification du magazine');
      this.isSubmitting = false;
    }
  }

  private updateMagazine(data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.magazineId) {
        reject('No magazine ID');
        return;
      }

      this.magazineService.updateMagazine(this.magazineId, data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isSubmitting = false;
            this.showSuccess('Magazine modifié avec succès');
            this.hasUnsavedChanges = false;
            this.router.navigate(['/listeMagazine']);
            resolve();
          },
          error: (error) => {
            console.error('Backend error:', error);
            this.showError('Erreur serveur', 'Impossible de modifier le magazine. Veuillez réessayer.');
            this.isSubmitting = false;
            reject(error);
          }
        });
    });
  }

  cancel(): void {
    if (this.hasUnsavedChanges) {
      Swal.fire({
        title: 'Changements non sauvegardés',
        text: 'Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Quitter sans sauvegarder',
        cancelButtonText: 'Rester',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate(['/listeMagazine']);
        }
      });
    } else {
      this.router.navigate(['/listeMagazine']);
    }
  }

  resetForm(): void {
    if (!this.hasUnsavedChanges) return;

    Swal.fire({
      title: 'Réinitialiser les modifications ?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, réinitialiser',
      cancelButtonText: 'Annuler',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        if (this.magazine) {
          this.magazineForm.patchValue({
            name: this.magazine.name || '',
            nbr_telechargement: this.magazine.nbr_telechargement ?? 0,
            downloadCount: (this.magazine as any).downloadCount ?? 0,
            pdfManualUrl: this.magazine.download_link || ''
          }, { emitEvent: false });
          
          this.imagePreview = this.magazine.imageUrl ?? null;
          this.imageFile = null;
          this.uploadedImageUrl = null;
          
          this.pdfFile = null;
          this.uploadedPdfUrl = null;
          this.pdfFileName = this.magazine.download_link ? this.extractFileNameFromUrl(this.magazine.download_link) : '';
          this.pdfInputMode = 'upload';
          
          this.hasUnsavedChanges = false;
          
          Swal.fire({
            icon: 'success',
            title: 'Modifications annulées',
            timer: 1500,
            showConfirmButton: false
          });
        }
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    if (!formGroup) return;
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private showSuccess(message: string): void {
    Swal.fire({
      icon: 'success',
      title: 'Succès !',
      text: message,
      timer: 2000,
      showConfirmButton: false,
      background: '#f8f9fa',
      color: '#28a745'
    });
  }

  private showError(title: string, text: string): void {
    Swal.fire({
      icon: 'error',
      title: title,
      text: text,
      confirmButtonColor: '#dc3545',
      background: '#f8f9fa',
      color: '#dc3545'
    });
  }

  private showWarning(title: string, text: string): void {
    Swal.fire({
      icon: 'warning',
      title: title,
      text: text,
      confirmButtonColor: '#ffc107',
      background: '#f8f9fa'
    });
  }

  get isFormValid(): boolean {
    return this.magazineForm?.valid || false;
  }

  get submitButtonText(): string {
    if (this.isUploading) return 'Téléchargement image...';
    if (this.isUploadingPdf) return 'Téléchargement PDF...';
    if (this.isSubmitting) return 'Modification en cours...';
    return 'Modifier';
  }

  get showImage(): boolean {
    return !!this.imagePreview;
  }

  getFileNameFromUrl(url: string): string {
    return this.awsService.extractFileName(url);
  }

  getImageFileName(): string {
    if (this.imageFile) {
      return this.imageFile.name;
    } else if (this.magazine?.imageUrl) {
      return this.getFileNameFromUrl(this.magazine.imageUrl);
    }
    return 'Aucune image';
  }
}